/**
 * GRN composer · right-rail "What happens on post" preview.
 *
 * Faithful Tailwind translation of the prototype's `.composer-preview` →
 * `.preview-card` → `.preview-doc` block:
 *
 *   - `.preview-head`      → raised strip, "What happens on post" + live-dot.
 *   - `.preview-doc-head`  → `.preview-from` (GRN number · "Against PO-XXXX" ·
 *                            vendor name) left; `.preview-doc-title` ("Goods
 *                            receipt" + received-date + Draft pill) right.
 *   - `.preview-meta-grid` → 2×2: Received by · PO fulfilment · Accepted lines ·
 *                            Shortfall.
 *   - `.preview-items`     → Item · Ordered · Accepted (accepted green when
 *                            full, amber when short; rejected meta-line in rose).
 *   - `.preview-totals`    → Total accepted lines · Units rejected · `.grand`
 *                            Draft matching amount.
 *   - `.preview-summary`   → "On post" bullet list + PO-status-after / matching
 *                            amount cells.
 *
 * Every figure is driven by live form state + deterministic qty/paise math.
 *
 * @see prototype/purchases/goods-receipt-new.html §"Right column · What happens on post"
 */
import { Check, FileText, TriangleAlert } from "lucide-react";
import { paiseToInr } from "../../../lib/format.js";
import type { GrnLineRow } from "../lib/form-schema.js";
import type { GrnVendor } from "./grn-vendor-section.js";
import {
  computeGrnTotals,
  milliToDisplay,
  qtyToMilli,
  rejectedMilli,
} from "../lib/grn-math.js";

interface GrnPreviewProps {
  grnNumber: string;
  poNumber: string | null;
  vendor: GrnVendor | null;
  receivedDate: string;
  receivedBy: string;
  lines: GrnLineRow[];
}

export function GrnPreview({
  grnNumber,
  poNumber,
  vendor,
  receivedDate,
  receivedBy,
  lines,
}: GrnPreviewProps) {
  const totals = computeGrnTotals(lines);
  const fullyReceived = totals.poFulfilment === "received";
  const hasPo = poNumber !== null;

  return (
    <aside className="sticky top-20 self-start">
      <div className="overflow-hidden rounded-md border border-line bg-paper shadow-card">
        {/* .preview-head */}
        <header className="flex items-center justify-between gap-3 border-b border-line bg-raised px-[18px] py-3.5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-emerald-500 shadow-[0_0_0_3px] shadow-emerald-50" />
            What happens on post
          </h3>
        </header>

        {/* .preview-doc */}
        <div className="px-[22px] pb-6 pt-[22px] text-xs leading-[1.55] text-ink-2">
          {/* .preview-doc-head */}
          <div className="mb-3.5 flex items-start justify-between gap-[18px] border-b border-line pb-3.5">
            <div className="min-w-0">
              <span className="mb-0.5 block text-sm font-bold tracking-tight text-ink">
                {grnNumber}
              </span>
              <span className="block text-xs leading-normal text-muted">
                Against {poNumber ?? "—"}
              </span>
              {vendor ? (
                <span className="block text-xs leading-normal text-ink-2">{vendor.name}</span>
              ) : null}
            </div>
            <div className="shrink-0 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted">
              Goods receipt
              <span className="mt-1 block font-mono text-sm font-bold normal-case tracking-tight text-ink">
                {formatDocDate(receivedDate)}
              </span>
              <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                <FileText className="h-2.5 w-2.5" />
                Draft
              </span>
            </div>
          </div>

          {/* .preview-meta-grid */}
          <div className="mb-3.5 grid grid-cols-2 gap-x-4 gap-y-2.5 border-b border-line pb-3.5">
            <MetaCell label="Received by">
              <strong className="font-semibold">{receivedBy || "—"}</strong>
            </MetaCell>
            <MetaCell label="PO fulfilment">
              {hasPo ? (
                <strong
                  className={`font-semibold ${fullyReceived ? "text-emerald-600" : "text-amber-600"}`}
                >
                  {fullyReceived ? "Fully received" : "Partially received"}
                </strong>
              ) : (
                <strong className="font-semibold">—</strong>
              )}
            </MetaCell>
            <MetaCell label="Accepted lines" mono>
              {totals.acceptedLineCount} of {totals.totalLineCount}
            </MetaCell>
            <MetaCell label="Shortfall" mono>
              {totals.rejectedMilli > 0n ? (
                <span className="text-rose-600">
                  {milliToDisplay(totals.rejectedMilli)} rejected
                </span>
              ) : (
                "None"
              )}
            </MetaCell>
          </div>

          {/* .preview-items */}
          <table className="mb-3 w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="border-b border-line py-1.5 text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Item
                </th>
                <th className="border-b border-line py-1.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Ordered
                </th>
                <th className="border-b border-line py-1.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Accepted
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-3 text-center text-xs text-muted">
                    Select a PO to see its lines here.
                  </td>
                </tr>
              ) : (
                lines.map((row, i) => {
                  const rejected = rejectedMilli(row);
                  const short = rejected > 0n;
                  return (
                    <tr
                      key={row.purchaseOrderLineId}
                      className={i < lines.length - 1 ? "border-b border-line" : ""}
                    >
                      <td className="py-1.5 align-top text-ink">
                        {row.description}
                        {short ? (
                          <span className="block text-xs text-rose-600">
                            {milliToDisplay(rejected)} rejected
                            {row.rejectReason ? ` — ${row.rejectReason}` : ""}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1.5 text-right align-top font-mono tabular-nums">
                        {milliToDisplay(qtyToMilli(row.ordered))}
                      </td>
                      <td
                        className={`py-1.5 text-right align-top font-mono font-semibold tabular-nums ${short ? "text-amber-600" : "text-emerald-600"}`}
                      >
                        {milliToDisplay(qtyToMilli(row.accepted))}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* .preview-totals */}
          <div className="ml-auto mt-1 max-w-[240px] border-t border-line pt-3">
            <TotalRow
              label="Total accepted lines"
              value={`${totals.acceptedLineCount} / ${totals.totalLineCount}`}
            />
            <TotalRow
              label="Units rejected"
              value={milliToDisplay(totals.rejectedMilli)}
              rose={totals.rejectedMilli > 0n}
            />
            <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-line pb-1 pt-2 text-sm font-bold text-ink">
              <span>Draft matching amount</span>
              <span className="font-mono text-sm font-bold tracking-tight tabular-nums">
                {paiseToInr(totals.matchingPaise)}
              </span>
            </div>
          </div>
        </div>

        {/* .preview-summary */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-line px-[18px] py-3.5">
          <div className="col-span-2 min-w-0">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">
              On post
            </span>
            {hasPo ? (
              <ul className="flex flex-col gap-1.5">
                <SummaryBullet tone="ok">
                  Updates {poNumber} fulfilment — now{" "}
                  {fullyReceived ? "fully received" : "partially received"}
                </SummaryBullet>
                <SummaryBullet tone="ok">
                  Creates a draft matching document{vendor ? ` for ${vendor.name}` : ""} (
                  {paiseToInr(totals.matchingPaise)}) — your team reviews and raises the vendor
                  payment from there
                </SummaryBullet>
                {totals.rejectedMilli > 0n ? (
                  <SummaryBullet tone="warn">
                    {milliToDisplay(totals.rejectedMilli)} units marked rejected — noted on the
                    matching document for follow-up with the vendor
                  </SummaryBullet>
                ) : null}
              </ul>
            ) : (
              <p className="text-xs text-muted">Pick a purchase order to preview the status change.</p>
            )}
          </div>
          <MetaCell label="PO status after">
            {hasPo ? (
              <strong
                className={`font-semibold ${fullyReceived ? "text-emerald-600" : "text-amber-600"}`}
              >
                {fullyReceived ? "Fully received" : "Partially received"}
              </strong>
            ) : (
              <strong className="font-semibold">—</strong>
            )}
          </MetaCell>
          <MetaCell label="Matching amount" mono>
            {paiseToInr(totals.matchingPaise)}
          </MetaCell>
        </div>
      </div>
    </aside>
  );
}

function MetaCell({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <span className="mb-0.5 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">
        {label}
      </span>
      <span
        className={`block text-xs leading-normal text-ink ${mono ? "font-mono tabular-nums" : ""}`}
      >
        {children}
      </span>
    </div>
  );
}

function TotalRow({ label, value, rose = false }: { label: string; value: string; rose?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-xs text-muted">
      <span>{label}</span>
      <span className={`font-mono font-medium tabular-nums ${rose ? "text-rose-600" : "text-ink"}`}>
        {value}
      </span>
    </div>
  );
}

function SummaryBullet({ tone, children }: { tone: "ok" | "warn"; children: React.ReactNode }) {
  const Icon = tone === "ok" ? Check : TriangleAlert;
  const colour = tone === "ok" ? "text-emerald-600" : "text-amber-600";
  return (
    <li className={`flex items-start gap-1.5 text-xs ${tone === "ok" ? "text-ink-2" : "text-warn"}`}>
      <Icon className={`mt-0.5 h-3 w-3 shrink-0 ${colour}`} strokeWidth={2.4} aria-hidden />
      <span className="min-w-0 flex-1">{children}</span>
    </li>
  );
}

/** ISO date → "12 May 2026" for the printable preview. "—" when absent. */
function formatDocDate(iso: string): string {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthLabel = months[Number(month) - 1] ?? month;
  return `${day} ${monthLabel} ${year}`;
}
